/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Spinner } from '@sonarsource/echoes-react';
import { useCurrentUser } from '~adapters/helpers/users';
import { JiraProjectBindingSetting } from '~feature-jira/components/project-setting/JiraProjectBindingSetting';
import { JIRA_INTEGRATION_ORGANIZATION_UUID } from '~feature-jira/helpers';
import { INSTANCE_INTEGRATIONS_CATEGORY } from '~sq-server-commons/constants/settings';
import { useAvailableFeatures } from '~sq-server-commons/context/available-features/withAvailableFeatures';
import { getGlobalSettingsUrl } from '~sq-server-commons/helpers/urls';
import { useProjectQuery } from '~sq-server-commons/queries/projects';
import { Feature } from '~sq-server-commons/types/features';
import { Permissions } from '~sq-server-commons/types/permissions';
import { Component } from '~sq-server-commons/types/types';

interface JiraProjectBindingProps {
  component: Component;
}

export function JiraProjectBinding(props: Readonly<JiraProjectBindingProps>) {
  const { component } = props;

  const { currentUser } = useCurrentUser();
  const { hasFeature } = useAvailableFeatures();
  const hasJiraIntegrationFeature = hasFeature(Feature.JiraIntegration);

  const { data: project, isLoading: isLoadingProject } = useProjectQuery(component.key);

  const isLoading = isLoadingProject;

  if (!hasJiraIntegrationFeature) {
    return null;
  }

  return (
    <Spinner isLoading={isLoading}>
      {project && (
        <JiraProjectBindingSetting
          isOrgAdmin={currentUser.permissions?.global?.includes(Permissions.Admin) ?? false}
          isSdlcDataReleaseOpenJiraTicketsEnabled
          orgBindingUrl={getGlobalSettingsUrl(INSTANCE_INTEGRATIONS_CATEGORY)}
          // No organization on SQS but the API implementation is aligned on SQC
          // so we use this 0 value as the organization UUID
          // (it's how it'll be saved in the DB so don't pass any value)
          organizationUuid={JIRA_INTEGRATION_ORGANIZATION_UUID}
          shouldCheckForBindingPermissions
          sonarProjectId={project.uuid ?? ''}
          sonarProjectName={project.name}
        />
      )}
    </Spinner>
  );
}
